/**
 * Skins (สกิน): purely cosmetic looks worn over the character. They change nothing in a fight.
 *
 * Slots (the wardrobe's, systems/wardrobe.ts): a whole fashion outfit (`outfit`), or one piece at a time: a hat or
 * hair ornament (`hat`), a hairstyle (`hair`: none worn = the creator's own hair), a top (`top`), trousers or a skirt
 * (`bottom`), shoes (`shoes`), and a back piece (`back`).
 * The pieces follow the 3D outfit slots (render3d/outfits slots.json): `top` is the torso, sleeves and sash, `bottom`
 * the legs, `shoes` the feet. A whole outfit covers the top, the bottom and the shoes unless it says otherwise; a
 * piece worn with it shows over it.
 */
import { FAME_SKINS } from './fameBoards';

export type SkinSlot = 'outfit' | 'hat' | 'hair' | 'top' | 'bottom' | 'shoes' | 'cape' | 'shoulders' | 'back';
export const SKIN_SLOTS: readonly SkinSlot[] = [
  'outfit',
  'hat',
  'hair',
  'top',
  'bottom',
  'shoes',
  'cape',
  'shoulders',
  'back',
];
export const SKIN_SLOT_LABELS: Readonly<Record<SkinSlot, string>> = {
  outfit: 'ชุดแฟชั่น',
  hat: 'หมวกและเครื่องประดับผม',
  hair: 'ทรงผม',
  top: 'เสื้อ',
  bottom: 'กางเกงและกระโปรง',
  shoes: 'รองเท้า',
  cape: 'ผ้าคลุม',
  shoulders: 'บ่า',
  back: 'แผ่นหลัง',
};
/** The piece slots (worn one at a time, under or over a whole outfit). */
export const PIECE_SLOTS: readonly SkinSlot[] = [
  'hat',
  'hair',
  'top',
  'bottom',
  'shoes',
  'cape',
  'shoulders',
];
/** What a whole fashion outfit covers when it does not say. */
export const OUTFIT_COVERS: readonly SkinSlot[] = ['top', 'bottom', 'shoes'];

export interface SkinDef {
  readonly id: string;
  readonly name: string;
  readonly slot: SkinSlot;
  readonly desc: string;
  /** Glow colour (rgb) the client draws it with. */
  readonly color: string;
  /** Appearance key worn over the character's clothes (fashion outfits). */
  readonly look?: string;
  /** Who it fits (data/appearance.ts `Sex`); absent: everyone. */
  readonly sex?: 'm' | 'f';
  /** A whole outfit: the piece slots it covers (default `OUTFIT_COVERS`). */
  readonly covers?: readonly SkinSlot[];
  /** Owned by everyone from the start (the plain everyday pieces, ชุดพื้นฐาน): never sold, never granted. */
  readonly free?: boolean;
}

/**
 * The NPCs' clothes, hats and hairs (NPC-SPLIT), free to every character of the NPC's sex: the 3D NPCs' own pieces
 * (models/npcs, split by layer; which parts each id wears is the client's, ui/wardrobeParts.ts). Ids `npc-<npc>` (the
 * whole outfit: robe and sleeves, sash, trousers, shoes, cape), `npc-<npc>-top` (robe, sleeves, sash), `-bottom`,
 * `-shoes`, `-hat` (hat, cap or headband), `-hair` (the hair slot: a hairstyle with its bun and pins) and `-cape`
 * (the cape slot).
 */
export const NPC_WEAR: readonly {
  readonly npc: string;
  readonly name: string;
  readonly sex: 'm' | 'f';
  readonly color: string;
  /**
   * Which pieces it has besides the outfit, top and shoes (the wardrobe wears them whole: models/outfits/npc-pieces,
   * the trousers under a long robe too).
   */
  readonly bottom: boolean;
  readonly hat: boolean;
  readonly hair: boolean;
  /** A cape of its own (the cape slot, ผ้าคลุม; the top leaves it). */
  readonly cape?: boolean;
}[] = [
  { npc: 'elder', name: 'ผู้เฒ่าหลิว', sex: 'm', color: '111,125,92', bottom: true, hat: false, hair: true },
  { npc: 'merchant', name: 'เถ้าแก่ฉาง', sex: 'm', color: '138,90,42', bottom: true, hat: true, hair: true },
  { npc: 'guide', name: 'ผู้นำทางเขา', sex: 'm', color: '106,90,68', bottom: true, hat: true, hair: true },
  {
    npc: 'taoist',
    name: 'ผู้เฒ่าชิงซง',
    sex: 'm',
    color: '217,221,227',
    bottom: true,
    hat: false,
    hair: true,
  },
  {
    npc: 'monk',
    name: 'ช่างใหญ่ฮุ่ยเหมิง',
    sex: 'm',
    color: '224,138,42',
    bottom: true,
    hat: false,
    hair: false,
  },
  {
    npc: 'guardOfficer',
    name: 'ผู้คุมทางหลู่เจิ้ง',
    sex: 'm',
    color: '138,28,34',
    bottom: true,
    hat: true,
    hair: true,
  },
  {
    npc: 'scholar',
    name: 'ผู้จดเหวินชิง',
    sex: 'm',
    color: '232,240,244',
    bottom: true,
    hat: false,
    hair: true,
  },
  {
    npc: 'bloodMaster',
    name: 'ประมุขรองเสวี่ยอู๋',
    sex: 'm',
    color: '122,12,20',
    bottom: true,
    hat: true,
    hair: true,
    cape: true,
  },
  {
    npc: 'blissLady',
    name: 'ผู้อาวุโสแห่งคืนเฝ้าเงาเหมย',
    sex: 'f',
    color: '58,26,74',
    bottom: false,
    hat: false,
    hair: true,
  },
  { npc: 'watchman', name: 'ลุงเจิ้ง', sex: 'm', color: '58,58,68', bottom: true, hat: true, hair: true },
  { npc: 'fisherOld', name: 'ลุงไห่', sex: 'm', color: '90,116,136', bottom: true, hat: true, hair: true },
  {
    npc: 'stationMaster',
    name: 'ลุงเฉิน',
    sex: 'm',
    color: '106,74,48',
    bottom: true,
    hat: true,
    hair: true,
  },
  { npc: 'farmer', name: 'อาหนิว', sex: 'm', color: '122,106,74', bottom: true, hat: true, hair: true },
  { npc: 'fisherKid', name: 'เสี่ยวหยู', sex: 'm', color: '184,68,58', bottom: true, hat: false, hair: true },
  { npc: 'innkeeper', name: 'นางหลิว', sex: 'f', color: '138,58,90', bottom: false, hat: false, hair: true },
  { npc: 'farmerAunt', name: 'ป้าซุน', sex: 'f', color: '62,74,122', bottom: false, hat: true, hair: true },
  { npc: 'teaSeller', name: 'ป้าฟาง', sex: 'f', color: '74,122,90', bottom: false, hat: false, hair: true },
  { npc: 'bandit', name: 'โจร', sex: 'm', color: '125,90,60', bottom: true, hat: true, hair: true },
];

const list: SkinDef[] = [
  ...FAME_SKINS,
  {
    id: 'dugu-robe',
    name: 'ชุดจอมมารกระบี่',
    slot: 'outfit',
    desc: 'ชุดประจำฉายาเถาฉางเยี่ย เสื้อคลุมดำขาดรุ่งริ่ง ชายแดงฉาน ผมยาวสยายดำขลับ แขนพันผ้า กระบี่หนักสีดำเล่มยาว',
    color: '200,30,40',
    look: 'fashionDugu',
  },
  {
    id: 'azure-fox',
    name: 'จิ้งจอกจันทร์คราม',
    slot: 'outfit',
    desc: 'ชุดจิ้งจอกจันทร์สีน้ำเงินครามซ้อนชั้นทับเสื้อขาว ผ้าคลุมขนจิ้งจอกขาวพาดไหล่ ผมสีเงินพร้อมหูจิ้งจอก ประดับทองห้อยพู่สีฟ้า',
    color: '58,138,224',
    look: 'fashionAzureFox',
  },
  {
    id: 'purple-dew',
    name: 'น้ำค้างม่วง',
    slot: 'outfit',
    desc: 'เสื้อคลุมยาวสีม่วงลายดอกเปิดหน้า ซับแขนแดง เสื้อในและกางเกงขาวพอง หมวกผ้าดำปักดอกแดง คาดเข็มขัดทองห้อยพู่',
    color: '122,74,192',
    look: 'fashionPurpleDew',
  },
  {
    id: 'violet-fan-swords',
    name: 'พัดกระบี่ม่วงราตรี',
    slot: 'back',
    desc: 'กระบี่สี่เล่มในฝักสีม่วงเรียงเป็นพัดบนแท่นประดับเงิน ด้ามประดับหยก ร้อยสร้อยลูกปัด พู่ขาวห้อยระย้า เรืองแสงสีม่วงดุจราตรีกาล',
    color: '176,104,255',
  },
  ...basics(),
  ...npcPieces(),
  ...fashionOutfits(),
];

/**
 * The fashion outfits of lane C0-OUTFIT (the client's render/rig/fashion.ts, looks `fashion-<id>`): ten full outfits,
 * each drawn for a man and for a woman. Free to everyone for now (testing, user-approved 2026-10-10); prices later.
 */
function fashionOutfits(): SkinDef[] {
  return (
    [
      ['wanderer', 'ชุดจอมยุทธ์พเนจร', 'ชุดเดินทางคราม หมวกงอบไม้ไผ่ ผ้าพันคอแดงยาวปลิว', '70,85,122'],
      ['mistpeak', 'ชุดศิษย์ยอดหมอก', 'ชุดนักพรตเทาหมอกขลิบหมึก แขนเสื้อกว้างห้อย ชายผ้าคาดเอวยาว', '201,207,210'],
      ['bellrock', 'ชุดศิษย์หินระฆัง', 'กางเกงพระสีหญ้าฝรั่น จีวรแดงเลือดหมูพาดบ่า ลูกประคำไม้', '201,120,42'],
      ['ironlantern', 'ชุดทางโคมเหล็ก', 'ชุดยาวดำเหล็กขลิบอำพัน ผ้าคลุมพาดบ่า หมวกขุนนางปีก', '216,138,42'],
      ['dimmoon', 'ชุดศิษย์จันทร์มัว', 'ม่วงราตรีขลิบเงินจันทร์ ผ้าแพรชมพู ชายผ้าพู่ห้อย', '58,32,80'],
      ['inkpine', 'ชุดศิษย์หอหมึกสน', 'ชุดบัณฑิตขาวกระดาษขลิบเขียวสน แขนเสื้อกว้างยาว หยกห้อยเอว', '62,106,74'],
      ['cinnabar', 'ชุดศิษย์ดาบชาด', 'ชุดศึกดำ ผ้าแดงชาด เกราะไหล่ข้างเดียว ผ้าคลุมพาดบ่า', '184,50,30'],
      ['guard', 'ชุดราชองครักษ์', 'เกราะเกล็ดเหล็กบนชุดแดงเลือดหมู หมวกเกราะ ผ้าคลุมยาว', '138,30,36'],
      ['nightblade', 'ชุดนักฆ่ารัตติกาล', 'ชุดรัดรูปดำม่วง ผ้าปิดหน้า ฮู้ดหรือผ้าพันคอแดง', '34,28,42'],
      ['hunter', 'ชุดพรานแดนเหนือ', 'เสื้อนวมหนังสัตว์ ผ้าคลุมขนครีมพาดบ่า บู๊ตขน', '122,82,54'],
    ] as const
  ).map(([id, name, desc, color]) => ({
    id: `fashion-${id}`,
    name,
    slot: 'outfit' as const,
    desc,
    color,
    look: `fashion-${id}`,
    free: true,
  }));
}

/**
 * The plain everyday pieces (ชุดพื้นฐาน), free to every character: the garments the women's outfit families already
 * have on Model 0 (tools/blender/outfits/women.py), and the men's (lane A, tools/blender/outfits/garments_m.py). Which 3D parts each one wears is the client's (lane OUTFIT-parts).
 */
function basics(): SkinDef[] {
  const piece = (
    slot: SkinSlot,
    key: string,
    name: string,
    color: string,
    sex: 'm' | 'f' | null = 'f',
  ): SkinDef => ({
    id: `basic-${key}`,
    name,
    slot,
    desc: `${name} ชุดพื้นฐาน สวมได้ทุกคน ย้อมสีได้ที่ตู้เสื้อผ้า`,
    color,
    ...(sex ? { sex } : {}),
    free: true,
  });
  // PARTS-ART's hats, hairstyles, capes and pauldrons (tools/blender/outfits/parts_art.py, models/outfits/parts.json),
  // ids `basic-hat-<key>`, `basic-hair-<key>`, `basic-cape-<key>`, `basic-shoulders-<key>`; the parts are the client's
  // (ui/wardrobeParts.ts).
  const partsArt = [
    ...(
      [
        ['douli', 'งอบไม้ไผ่', '196,164,104'],
        ['straw', 'งอบฟางสาน', '210,162,85'],
        ['veil', 'หมวกม่านโปร่ง', '246,236,232'],
        ['scholar', 'หมวกบัณฑิต', '47,33,36'],
        ['jadecrown', 'มงกุฎหยก', '210,162,85'],
        ['heavencrown', 'มงกุฎบันไดสวรรค์', '240,210,140'],
        ['redband', 'ผ้าคาดศีรษะแดง', '189,33,41'],
        ['turban', 'ผ้าโพกไหม', '236,218,215'],
        ['helm', 'หมวกเหล็กบุผ้า', '169,182,200'],
        ['foxears', 'หูจิ้งจอก', '246,236,232'],
        ['flowercap', 'หมวกผ้าดำปักดอก', '47,33,36'],
      ] as const
    ).map(([k, name, color]) => piece('hat', `hat-${k}`, name, color, null)),
    ...(
      [
        ['topknot', 'มวยจุกปักปิ่น', 'm'],
        ['halfdown', 'ผมยาวรวบครึ่งหัว', 'm'],
        ['lowtail', 'หางม้าต่ำพเนจร', 'm'],
        ['twinbuns', 'มวยคู่ผูกริบบิ้น', 'f'],
        ['ponytail', 'หางม้าสูงผูกริบบิ้น', 'f'],
        ['longloose', 'ผมยาวสยายมวยครึ่ง', 'f'],
      ] as const
    ).map(([k, name, sex]) => piece('hair', `hair-${k}`, name, '58,36,35', sex)),
    piece('cape', 'cape-short', 'ผ้าคลุมเดินทางสั้น', '98,32,44', null),
    piece('cape', 'cape-long', 'ผ้าคลุมยาว', '98,32,44', null),
    piece('shoulders', 'shoulders-light', 'บ่าเกราะเบา', '67,48,45', null),
    // the schools' signature collars (round 5, parts-*-d, slot `collar`): free, any school's look on any outfit
    ...(
      [
        ['mist-peak', 'ปกเมฆยอดหมอก', '236,218,215'],
        ['bell-stone', 'จีวรซ้อนชั้นหินระฆัง', '217,136,58'],
        ['lantern-road', 'คอตั้งชุดในดำ', '98,32,44'],
        ['dim-moon', 'บ่าดอกไม้จันทร์มัว', '200,96,142'],
        ['pine-ink', 'ผ้าพาดบัณฑิต', '84,140,98'],
        ['cinnabar', 'บ่าซี่เลื่อยดาบชาด', '168,30,38'],
      ] as const
    ).map(([k, name, color]) => piece('shoulders', `shoulders-collar-${k}`, name, color, null)),
  ];
  // The men's everyday pieces: lane A's robes and tops on Model 0 man (tools/blender/outfits/garments_m.py,
  // models/outfits/men.json), ids `basic-m-<key>`.
  const NAVY = '59,85,163';
  const menTops = (
    [
      ['cross', 'เสื้อป้ายไขว้'],
      ['tunic', 'เสื้อยาวคลุมต้นขา'],
      ['short', 'เสื้อแขนสั้น'],
      ['vest', 'เสื้อกั๊กแขนกุด'],
      ['rolled', 'เสื้อพับแขน'],
      ['patched', 'เสื้อปะ'],
      ['wide', 'เสื้อแขนกว้าง'],
      ['mandarin', 'เสื้อคอตั้ง'],
      ['bracer', 'เสื้อรัดข้อมือ'],
      ['lamellar', 'เสื้อเกราะเกล็ด'],
      ['quilted', 'เสื้อนวม'],
      ['bare', 'เสื้อเปลือยไหล่'],
      ['open', 'เสื้อเปิดอก'],
      ['longvest', 'เสื้อกั๊กยาว'],
      ['armoured', 'เสื้อเกราะแขนยาว'],
    ] as const
  ).map(([key, name]) => piece('top', `m-${key}`, name, NAVY, 'm'));
  const menRobes = (
    [
      ['robe', 'ชุดคลุมยาว'],
      ['coat', 'เสื้อคลุมยาวแขนกุด'],
      ['travel', 'ชุดเดินทาง'],
      ['hanfu', 'ฮั่นฝู'],
      ['daopao', 'ชุดนักพรต'],
      ['kasaya', 'จีวรพาดบ่า'],
      ['feiyu', 'ชุดปลาบิน'],
      ['battle', 'ชุดศึกผ่าหน้า'],
      ['scholar', 'ชุดบัณฑิต'],
      ['furcloak', 'เสื้อคลุมคอขน'],
      ['court', 'ชุดขุนนาง'],
      ['armour', 'ชุดเกราะยาว'],
      ['hermit', 'ชุดฤๅษีปะ'],
    ] as const
  ).map(([key, name]) => ({ ...piece('outfit', `m-${key}`, name, NAVY, 'm'), covers: ['top'] as const }));
  return [
    piece('top', 'ao', 'เสื้อป้ายสตรีปักดอก', '214,96,120'),
    piece('top', 'crop', 'เสื้อเอวลอย', '226,120,90'),
    piece('top', 'puff', 'เสื้อแขนพอง', '240,190,120'),
    piece('top', 'swordmaid', 'เสื้อจอมยุทธหญิง', '189,33,41'),
    piece('top', 'sleevelessf', 'เสื้อแขนกุดคอตั้ง', '98,32,44'),
    piece('top', 'beizi', 'เสื้อคลุมยาวผ่าหน้า', '120,150,200'),
    piece('top', 'cloud', 'เสื้อปักเมฆ', '160,200,230'),
    piece('top', 'longstand', 'เสื้อคอตั้งตัวยาว', '90,130,110'),
    piece('top', 'bandsleeve', 'เสื้อแขนกว้างขลิบชาย', '210,162,85'),
    piece('top', 'quiltedf', 'เสื้อนวมสตรี', '170,120,90'),
    piece('bottom', 'skirt', 'กระโปรงยาว', '236,218,215'),
    piece('bottom', 'midi', 'กระโปรงยาวครึ่งแข้ง', '220,180,190'),
    piece('bottom', 'shortskirt', 'กระโปรงสั้น', '200,90,100'),
    piece('bottom', 'layered', 'กระโปรงสองชั้น', '189,33,41'),
    piece('bottom', 'pleated', 'กระโปรงจีบ', '98,32,44'),
    piece('bottom', 'flowerskirt', 'กระโปรงปักดอก', '230,150,170'),
    piece('bottom', 'cloudskirt', 'กระโปรงลายเมฆ', '150,190,220'),
    piece('bottom', 'splitskirt', 'กระโปรงผ่าหน้า', '80,60,90'),
    ...(
      [
        ['ruqun', 'ชุดสตรีเอวสูง', '214,96,120'],
        ['trailing', 'ชุดหางยาว', '236,218,215'],
        ['palace', 'ชุดนางใน', '210,162,85'],
        ['swordlady', 'ชุดจอมยุทธหญิง', '189,33,41'],
        ['fairy', 'ชุดเซียน', '200,230,240'],
        ['dancer', 'ชุดนางรำ', '230,120,150'],
        ['priestess', 'ชุดนักพรตหญิง', '120,110,160'],
        ['furcape', 'เสื้อคลุมขนสตรี', '240,236,228'],
        ['longopen', 'ชุดคลุมเปิดยาว', '90,130,110'],
        ['snow', 'ชุดขาวหิมะ', '244,241,234'],
      ] as const
    ).map(([key, name, color]) => ({ ...piece('outfit', key, name, color), covers: ['top'] as const })),
    ...menTops,
    ...menRobes,
    ...partsArt,
  ];
}

function npcPieces(): SkinDef[] {
  const out: SkinDef[] = [];
  for (const n of NPC_WEAR) {
    const piece = (id: string, slot: SkinSlot, what: string, extra: Partial<SkinDef> = {}): SkinDef => ({
      id,
      name: `${what}${n.name}`,
      slot,
      desc: `${what}แบบเดียวกับของ${n.name} สวมได้ฟรีสำหรับตัวละคร${n.sex === 'm' ? 'ชาย' : 'หญิง'}`,
      color: n.color,
      sex: n.sex,
      free: true,
      ...extra,
    });
    const covers: SkinSlot[] = n.bottom ? ['top', 'bottom', 'shoes'] : ['top', 'shoes'];
    out.push(piece(`npc-${n.npc}`, 'outfit', 'ชุด', { covers }));
    out.push(piece(`npc-${n.npc}-top`, 'top', 'เสื้อ'));
    if (n.bottom) out.push(piece(`npc-${n.npc}-bottom`, 'bottom', 'กางเกง'));
    out.push(piece(`npc-${n.npc}-shoes`, 'shoes', 'รองเท้า'));
    if (n.hat) out.push(piece(`npc-${n.npc}-hat`, 'hat', 'หมวก'));
    if (n.hair) out.push(piece(`npc-${n.npc}-hair`, 'hair', 'ทรงผม'));
    if (n.cape) out.push(piece(`npc-${n.npc}-cape`, 'cape', 'ผ้าคลุม'));
  }
  return out;
}

export const SKINS: Readonly<Record<string, SkinDef>> = Object.fromEntries(list.map((s) => [s.id, s]));

export function getSkin(id: string): SkinDef {
  const s = SKINS[id];
  if (!s) throw new Error(`Unknown skin: ${id}`);
  return s;
}

/** Skins every character owns from the start (the rest come from the item mall or titles; `free` ones are owned by all). */
export const STARTER_SKINS: string[] = [];

/** Whether the character owns a skin: bought, granted, or free to all. */
export const ownsSkin = (owned: readonly string[], id: string): boolean =>
  owned.includes(id) || SKINS[id]?.free === true;

/** Whether a skin fits a character of this sex. */
export const skinFits = (id: string, sex: 'm' | 'f'): boolean => {
  const s = SKINS[id];
  return !!s && (!s.sex || s.sex === sex);
};

/**
 * Where a skin saved under `slot` is worn now, or null when it no longer fits there: its own slot, or `hair` for a
 * hairstyle saved in the hat slot before the hair slot was its own (the NPC hairs, 2026-10-08).
 */
export function savedSkinSlot(slot: string, id: string): SkinSlot | null {
  const own = SKINS[id]?.slot;
  if (!own) return null;
  return own === slot || (slot === 'hat' && own === 'hair') ? own : null;
}

/** The piece slots a whole outfit covers. */
export const coversOf = (id: string): readonly SkinSlot[] => SKINS[id]?.covers ?? OUTFIT_COVERS;
